import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Package, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { pluralize } from '@/lib/utils';
import { DetailLayout, DetailList, RailCard } from '@/components/common/DetailPanels';
import {
  PageHeader,
  DataTable,
  Column,
  StatusBadge,
  MetricCard,
  LoadingState,
  KPIStrip,
} from '@/components/common';
import { locationsApi, Location } from '@/api/services';
import { getErrorMessage } from '@/api/client';

const locationTypeColors: Record<string, string> = {
  HUB: 'bg-primary-50 text-primary-500',
  SITE: 'bg-success-50 text-success-600',
  DEPOT: 'bg-warning-50 text-warning-600',
  SUPPLIER: 'bg-status-neutral-subtle text-status-neutral-foreground',
};

const statusMap: Record<string, 'optimal' | 'low' | 'stockout' | 'overstock'> = {
  Optimal: 'optimal',
  Low: 'low',
  Stockout: 'stockout',
  Overstock: 'overstock',
};

interface LocationInventory {
  id: string;
  itemId: string;
  item: {
    id: string;
    sku: string;
    name: string;
    unit: string;
    category: string;
    unitCost?: number;
  };
  quantity: number;
  reservedQty: number;
  availableQty: number;
  status?: string;
}

export default function LocationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [location, setLocation] = useState<Location | null>(null);
  const [inventory, setInventory] = useState<LocationInventory[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [locationData, inventoryData] = await Promise.all([
        locationsApi.getById(id),
        locationsApi.getInventory(id),
      ]);
      setLocation(locationData);
      setInventory(inventoryData.data || inventoryData || []);
    } catch (error) {
      toast.error(getErrorMessage(error));
      navigate('/locations');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [id, fetchData]);

  if (loading) {
    return <LoadingState />;
  }

  if (!location) {
    return null;
  }

  const totalItems = inventory.length;
  const totalQuantity = inventory.reduce((sum, inv) => sum + inv.quantity, 0);
  const totalValue = inventory.reduce(
    (sum, inv) => sum + inv.quantity * (inv.item?.unitCost || 0),
    0
  );
  const lowStockCount = inventory.filter(
    (inv) => inv.status === 'Low' || inv.status === 'Stockout'
  ).length;

  const inventoryColumns: Column<LocationInventory>[] = [
    {
      key: 'item',
      header: 'Item',
      render: (inv) => (
        <div>
          <div className="font-medium">{inv.item?.name || '-'}</div>
          <div className="text-xs leading-4 text-muted-foreground">{inv.item?.sku}</div>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (inv) => <Badge variant="secondary">{inv.item?.category || '-'}</Badge>,
    },
    {
      key: 'quantity',
      header: 'Quantity',
      render: (inv) => (
        <span className="font-medium">
          {inv.quantity} {inv.item?.unit}
        </span>
      ),
    },
    {
      key: 'reserved',
      header: 'Reserved',
      render: (inv) => (
        <span className="text-muted-foreground">
          {inv.reservedQty} {inv.item?.unit}
        </span>
      ),
    },
    {
      key: 'available',
      header: 'Available',
      render: (inv) => (
        <span className="font-medium text-success-600">
          {inv.availableQty} {inv.item?.unit}
        </span>
      ),
    },
    {
      key: 'value',
      header: 'Value',
      render: (inv) => (
        <span className="text-muted-foreground">
          ${(inv.quantity * (inv.item?.unitCost || 0)).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (inv) => <StatusBadge status={statusMap[inv.status || 'Optimal'] || 'optimal'} />,
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          backLink={{ label: 'Back to Locations', href: '/locations' }}
          title={location.name}
          description={`Code: ${location.code}`}
          actions={
            <Button variant="outline">
              <Edit className="mr-2 h-4 w-4" />
              Edit Location
            </Button>
          }
        />
      </motion.div>

      {/* KPI Cards */}
      <div className="mt-6">
        <KPIStrip columns={4}>
          <MetricCard
            label="Total Items"
            value={totalItems}
            sentiment="info"
            icon={<Package className="h-5 w-5" />}
          />
          <MetricCard
            label="Total Quantity"
            value={totalQuantity.toLocaleString()}
            sentiment="neutral"
          />
          <MetricCard
            label="Inventory Value"
            value={`$${totalValue.toLocaleString()}`}
            sentiment="success"
          />
          <MetricCard
            label="Low Stock"
            value={lowStockCount}
            sentiment={lowStockCount > 0 ? 'warning' : 'success'}
          />
        </KPIStrip>
      </div>

      {/* Two columns: what is stocked here is the reason to open a location, so
          it takes the main column. The address, contact and coordinates are
          reference — they belong in the rail, where they stay visible while the
          table is scrolled. Previously the stub map held the wide column and the
          inventory table sat below the fold. */}
      <motion.div variants={fadeUp} className="mt-6">
        <DetailLayout
          main={
            <Card>
              <CardHeader>
                <h3 className="text-base font-semibold text-foreground">
                  Inventory at this location
                </h3>
                <CardDescription>
                  {inventory.length} {pluralize(inventory.length, 'item')} stocked here
                </CardDescription>
              </CardHeader>
              <CardContent>
                <DataTable
                  pagination
                  data={inventory}
                  columns={inventoryColumns}
                  onRowClick={(inv) => navigate(`/inventory/${inv.itemId}`)}
                  emptyTitle="No inventory"
                  emptyDescription="No items have been stocked at this location."
                />
              </CardContent>
            </Card>
          }
          rail={
            <>
              <RailCard
                title="Location details"
                icon={<MapPin className="h-4 w-4 text-muted-foreground" />}
              >
                <DetailList
                  items={[
                    {
                      label: 'Status',
                      value: <StatusBadge status={location.isActive ? 'active' : 'inactive'} />,
                    },
                    {
                      label: 'Type',
                      value: (
                        <Badge className={locationTypeColors[location.type] || ''}>
                          {location.type}
                        </Badge>
                      ),
                    },
                    { label: 'Code', value: location.code, when: !!location.code },
                    {
                      label: 'Shipments out',
                      value: location._count?.shipmentOrigins ?? 0,
                    },
                    {
                      label: 'Shipments in',
                      value: location._count?.shipmentDestinations ?? 0,
                    },
                  ]}
                />

                {location.address && (
                  <div className="mt-4 border-t pt-3">
                    <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Address
                    </p>
                    <div className="text-sm text-muted-foreground">
                      {location.address.street && <p>{location.address.street}</p>}
                      <p>
                        {[
                          location.address.city,
                          location.address.state,
                          location.address.postalCode,
                        ]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                      {location.address.country && <p>{location.address.country}</p>}
                    </div>
                  </div>
                )}

                {location.contact && (
                  <div className="mt-4 border-t pt-3">
                    <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Contact
                    </p>
                    <div className="space-y-0.5 text-sm text-muted-foreground">
                      {location.contact.name && <p>{location.contact.name}</p>}
                      {location.contact.email && <p>{location.contact.email}</p>}
                      {location.contact.phone && <p>{location.contact.phone}</p>}
                    </div>
                  </div>
                )}
              </RailCard>

              <RailCard
                title="Coordinates"
                icon={<MapPin className="h-4 w-4 text-muted-foreground" />}
              >
                {location.coordinates ? (
                  <p className="text-sm tabular-nums">
                    {location.coordinates.lat}, {location.coordinates.lng}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">No coordinates set</p>
                )}
              </RailCard>
            </>
          }
        />
      </motion.div>
    </motion.main>
  );
}
